import { DiscordSDK, Events } from '@discord/embedded-app-sdk';

/**
 * Thin wrapper around the Embedded App SDK. Outside Discord (no `frame_id` in the URL) it
 * becomes a mock with a local fake user so the game can be developed in a normal browser.
 */

export interface DiscordUser {
  id: string;
  username: string;
  global_name?: string | null;
  avatar?: string | null;
}

export interface Participant {
  id: string;
  name: string;
  avatarUrl?: string;
}

export type LayoutMode = 'focused' | 'pip' | 'grid';

export interface DiscordBridge {
  readonly inDiscord: boolean;
  readonly instanceId: string;
  readonly channelId: string | null;
  readonly guildId: string | null;
  readonly user: DiscordUser;
  readonly accessToken: string | null;
  /** '/.proxy' inside Discord, '' in a plain browser. Prefix every request to our own server with it. */
  readonly apiBase: string;
  participants(): Promise<Participant[]>;
  onParticipants(cb: (p: Participant[]) => void): () => void;
  onLayoutMode(cb: (mode: LayoutMode) => void): () => void;
  onSpeaking(cb: (userId: string, speaking: boolean) => void): () => void;
  setActivity(activity: ActivityPresence): Promise<void>;
  openInviteDialog(): Promise<void>;
  shareMoment(mediaUrl: string): Promise<void>;
}

export interface ActivityPresence {
  details?: string;
  state?: string;
  party?: { size: [number, number] };
  startedAt?: number;
  largeImage?: string;
  largeText?: string;
  smallImage?: string;
  smallText?: string;
}

export const CLIENT_ID: string = (import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined) ?? '';

export function isInsideDiscord(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('frame_id') || params.has('instance_id');
}

export function avatarUrlFor(user: DiscordUser, size = 128): string | undefined {
  if (user.avatar) return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.webp?size=${size}`;
  if (!/^\d+$/.test(user.id)) return undefined;
  const index = Number((BigInt(user.id) >> 22n) % 6n);
  return `https://cdn.discordapp.com/embed/avatars/${index}.png`;
}

function toParticipant(user: DiscordUser): Participant {
  return { id: user.id, name: user.global_name || user.username, avatarUrl: avatarUrlFor(user) };
}

/** The server's copy of the application id, for builds that did not bake one in. */
async function runtimeClientId(): Promise<string> {
  try {
    const res = await fetch('/.proxy/api/config');
    if (!res.ok) return '';
    const cfg = (await res.json()) as { clientId?: string };
    return cfg.clientId ?? '';
  } catch {
    return '';
  }
}

async function setupReal(): Promise<DiscordBridge> {
  const clientId = CLIENT_ID || (await runtimeClientId());
  if (!clientId) throw new Error('VITE_DISCORD_CLIENT_ID is not set (neither at build time nor on the server)');
  const sdk = new DiscordSDK(clientId);
  await sdk.ready();

  const { code } = await sdk.commands.authorize({
    client_id: clientId,
    response_type: 'code',
    state: '',
    prompt: 'none',
    scope: ['identify', 'guilds', 'rpc.activities.write'],
  });
  const tokenRes = await fetch('/.proxy/api/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!tokenRes.ok) throw new Error(`token exchange failed (${tokenRes.status})`);
  const { access_token } = (await tokenRes.json()) as { access_token: string };
  const auth = await sdk.commands.authenticate({ access_token });
  if (!auth) throw new Error('authenticate failed');

  const user: DiscordUser = {
    id: auth.user.id,
    username: auth.user.username,
    global_name: auth.user.global_name ?? null,
    avatar: auth.user.avatar ?? null,
  };

  const layoutNames: Record<number, LayoutMode> = { 0: 'focused', 1: 'pip', 2: 'grid' };

  return {
    inDiscord: true,
    instanceId: sdk.instanceId,
    channelId: sdk.channelId,
    guildId: sdk.guildId,
    user,
    accessToken: access_token,
    apiBase: '/.proxy',
    async participants() {
      const res = await sdk.commands.getInstanceConnectedParticipants();
      return res.participants.map(toParticipant);
    },
    onParticipants(cb) {
      const handler = (e: { participants: DiscordUser[] }) => cb(e.participants.map(toParticipant));
      sdk.subscribe(Events.ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE, handler);
      return () => void sdk.unsubscribe(Events.ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE, handler);
    },
    onLayoutMode(cb) {
      const handler = (e: { layout_mode: number }) => cb(layoutNames[e.layout_mode] ?? 'focused');
      sdk.subscribe(Events.ACTIVITY_LAYOUT_MODE_UPDATE, handler);
      return () => void sdk.unsubscribe(Events.ACTIVITY_LAYOUT_MODE_UPDATE, handler);
    },
    onSpeaking(cb) {
      // Needs rpc.voice.read, which unapproved apps don't get; fail quietly.
      if (!sdk.channelId) return () => {};
      const start = (e: { user_id: string }) => cb(e.user_id, true);
      const stop = (e: { user_id: string }) => cb(e.user_id, false);
      const args = { channel_id: sdk.channelId };
      Promise.all([
        sdk.subscribe(Events.SPEAKING_START, start, args),
        sdk.subscribe(Events.SPEAKING_STOP, stop, args),
      ]).catch(() => {});
      return () => {
        sdk.unsubscribe(Events.SPEAKING_START, start, args).catch(() => {});
        sdk.unsubscribe(Events.SPEAKING_STOP, stop, args).catch(() => {});
      };
    },
    async setActivity(a) {
      try {
        await sdk.commands.setActivity({
          activity: {
            type: 0,
            details: a.details,
            state: a.state,
            party: a.party ? { size: a.party.size } : undefined,
            timestamps: a.startedAt ? { start: a.startedAt } : undefined,
            assets: {
              large_image: a.largeImage,
              large_text: a.largeText,
              small_image: a.smallImage,
              small_text: a.smallText,
            },
          },
        });
      } catch (err) {
        console.warn('[presence] setActivity failed', err);
      }
    },
    async openInviteDialog() {
      try {
        await sdk.commands.openInviteDialog();
      } catch (err) {
        console.warn('[invite] failed', err);
      }
    },
    async shareMoment(mediaUrl) {
      try {
        await sdk.commands.openShareMomentDialog({ mediaUrl });
      } catch (err) {
        console.warn('[share] failed', err);
      }
    },
  };
}

function setupMock(): DiscordBridge {
  const params = new URLSearchParams(window.location.search);
  // Outside Discord you play as a guest. The id sticks to this browser so progression persists.
  let devId = params.get('dev') ?? '';
  if (!devId) {
    try {
      devId = localStorage.getItem('cratered.guestId') ?? '';
      if (!devId) {
        devId = Math.random().toString(36).slice(2, 10);
        localStorage.setItem('cratered.guestId', devId);
      }
    } catch {
      devId = Math.random().toString(36).slice(2, 10);
    }
  }
  const name = params.get('name') ?? `Mole ${devId.slice(0, 4)}`;
  const user: DiscordUser = { id: `dev-${devId}`, username: name, global_name: name, avatar: null };
  const instanceId = params.get('room') ?? 'local';
  const self: Participant = { id: user.id, name };
  return {
    inDiscord: false,
    instanceId,
    channelId: null,
    guildId: null,
    user,
    accessToken: null,
    apiBase: '',
    participants: async () => [self],
    onParticipants: () => () => {},
    onLayoutMode: () => () => {},
    onSpeaking: () => () => {},
    async setActivity(a) {
      console.debug('[presence:mock]', a.details, '·', a.state);
    },
    async openInviteDialog() {
      console.info('[invite:mock] share this URL with ?room=' + instanceId);
    },
    async shareMoment(url) {
      console.info('[share:mock]', url);
    },
  };
}

export async function setupDiscord(): Promise<DiscordBridge> {
  return isInsideDiscord() ? setupReal() : setupMock();
}
